import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';
import { fromCanonicalMarkupType } from '../../../utils/quotationUtils';

// Default categories if category bands not loaded from Masters (Price Range mode)
const DEFAULT_ROOM_CATEGORIES = [
  'Budget', 'Standard', 'Deluxe', 'Superior', 'Premium', 
  'Super Premium', 'Luxury', 'Super Luxury', 'Ultra Luxury'
];

// Star category tiers (Star Category mode) - default fallback, will be replaced by API data
const STAR_CATEGORY_TIERS = [
  { label: '3★', value: 3 },
  { label: '4★', value: 4 },
  { label: '5★', value: 5 },
];

const MEAL_PLANS = [
  { value: 'CP', label: 'Breakfast Only' },
  { value: 'MAP', label: 'Breakfast + Dinner' },
  { value: 'AP', label: 'All Meals' },
  { value: 'EP', label: 'Room Only' },
];

export const useQuotationForm = () => {
  const { user, isB2BAgent, showCostBreakdown } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [places, setPlaces] = useState([]);
  const [travelAgents, setTravelAgents] = useState([]);
  const [calculationResult, setCalculationResult] = useState(null);
  const [availableMealPlans, setAvailableMealPlans] = useState(MEAL_PLANS);
  const [availableCategories, setAvailableCategories] = useState(DEFAULT_ROOM_CATEGORIES);
  const [categoryBands, setCategoryBands] = useState([]); // Store the full category band data with min/max rates
  const [starCategoryTiers, setStarCategoryTiers] = useState(STAR_CATEGORY_TIERS); // Star tiers
  const [classificationMode, setClassificationMode] = useState('PRICE_RANGE'); // "STAR" or "PRICE_RANGE"
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]); // Track selected vehicle types for uplift
  const [customPackageHotelSelections, setCustomPackageHotelSelections] = useState({});
  const [masters, setMasters] = useState([]);

  const getInitialFormData = () => ({
    guest_name: '',
    guest_phone: '',
    guest_country_code: '+91',
    guest_email: '',
    travel_agent_id: null,
    route_type: 'predefined',
    route_id: null,
    custom_route: [],
    custom_package_data: null,
    arrival_city: '',
    arrival_date: '',
    arrival_point: 'Airport',
    arrival_remarks: '',
    departure_city: '',
    departure_point: 'Airport',
    departure_remarks: '',
    no_adults: 2,
    no_children_with_bed: 0,
    no_children_no_bed: 0,
    no_infants: 0,
    auto_suggest_rooms: true,
    no_single_rooms: 0,
    no_double_rooms: 1,
    no_triple_rooms: 0,
    selected_categories: ['Deluxe'],
    hotel_category: 'Deluxe',
    classification_mode: 'PRICE_RANGE', // "STAR" or "PRICE_RANGE"
    meal_plans: ['CP'],
    vehicle_type_selected: null,
    transfer_days: null,
    markup_type: 'percent', // 'percent' or 'amount'
    markup_pct: isB2BAgent ? 0 : 20,
    markup_amount: 0,
    markup_per_person: false, // if true, markup_amount is per person
    discount_type: 'percent', // 'percent' or 'amount'
    discount_pct: 0,
    discount_amount: 0,
    discount_per_person: false, // if true, discount_amount is per person
    show_discount_separately: false, // if true, show discount as a separate line item
    gst_percentage: 0,
    gst_included: true, // if false, show GST separately
    gst_apply_on: 'total', // 'total' or 'markup' - apply GST on total package or only markup amount
    show_misc_table: true, // Show miscellaneous items as separate table
    itinerary_type: 'FIXED',
    // Hotel/Transfer split defaults ON for everyone (B2B included — they
    // can't reach the toggle, and must always see it).
    // Ticket #285275 (#8): break-up OFF by default for Admin/Staff; B2B keeps
    // it ON because they can't reach the toggle and must see Hotel/Transfer.
    show_breakup: isB2BAgent,
    inclusions: '',
    exclusions: '',
    payment_terms: '',
    cancellation_policy: '',
    terms_conditions: '',
    quotation_format: 'total', // 'total' or 'person_wise'
    pdf_theme: 'regular', // 'short', 'regular', 'detailed'
    misc_costs: [], // [{remarks, cost_type, amount, currency, show_amount_breakup, is_optional}]
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // Fetch functions
  const fetchRoutes = async () => {
    try {
      const response = await api.get('/routes');
      setRoutes(response.data);
    } catch (error) {
      toast.error('Failed to load routes');
    }
  };

  const fetchPlaces = async () => {
    try {
      const response = await api.get('/places');
      setPlaces(response.data);
    } catch (error) {
      toast.error('Failed to load places');
    }
  };

  const fetchTravelAgents = async () => {
    try {
      const response = await api.get('/travel-agents');
      setTravelAgents(response.data);
    } catch (error) {
      console.error('Failed to load travel agents');
    }
  };

  const fetchVehicleOptions = async () => {
    // Fetch all transfer rates
    try {
      const response = await api.get('/transfers');
      setAllTransferRates(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load vehicle options');
    }
  };

  // Store all transfer rates for filtering
  const [allTransferRates, setAllTransferRates] = useState([]);

  // Calculate vehicle options based on route selection
  // Priority: 1. Route-based rates, 2. City-to-City rates, 3. Any-to-Any rates
  const getVehicleOptionsForRoute = useCallback((routeId, arrivalCity, departureCity, transferDays) => {
    if (!allTransferRates.length) return [];
    
    const days = transferDays || formData.no_nights + 1 || 1;
    const vehicleMap = new Map();
    
    // Helper to add vehicle to map with calculated total
    const addVehicle = (rate, priority, isPerRoute = false) => {
      const vehicleType = rate.vehicle_type;
      const existing = vehicleMap.get(vehicleType);
      
      // Only update if this is higher priority (lower number = higher priority)
      if (!existing || existing.priority > priority) {
        const rateAmount = rate.rate || 0;
        const total = isPerRoute ? rateAmount : (rateAmount * days);
        
        vehicleMap.set(vehicleType, {
          transfer_id: rate.transfer_rate_id,
          vehicle_type: vehicleType,
          vehicle_label: rate.vehicle_label,
          capacity: rate.max_pax_capacity,
          rate_amount: isPerRoute ? Math.round(rateAmount / days) : rateAmount, // Show per-day rate for display
          total: total,
          contract_type: rate.contract_type,
          is_per_route: isPerRoute,
          priority: priority,
          supplier_name: rate.supplier_name,
          gst_slab: rate.gst_slab ?? 0
        });
      }
    };
    
    // Priority 1: Route-based rates (PER_ROUTE)
    if (routeId) {
      const routeRates = allTransferRates.filter(t => 
        t.route_id === routeId && t.contract_type === 'PER_ROUTE'
      );
      routeRates.forEach(rate => addVehicle(rate, 1, true));
    }
    
    // Priority 2: City-to-City rates (PER_DAY)
    if (arrivalCity && departureCity) {
      const cityRates = allTransferRates.filter(t => 
        t.transfer_type === 'CITY_TO_CITY' &&
        t.contract_type === 'PER_DAY' &&
        t.arrival_city === arrivalCity &&
        t.departure_city === departureCity
      );
      cityRates.forEach(rate => addVehicle(rate, 2, false));
    }
    
    // Priority 3: Any-to-Any rates (PER_DAY)
    const anyToAnyRates = allTransferRates.filter(t => 
      t.transfer_type === 'CITY_TO_CITY' &&
      t.contract_type === 'PER_DAY' &&
      (t.arrival_city === 'Any' || t.arrival_city === null) &&
      (t.departure_city === 'Any' || t.departure_city === null)
    );
    anyToAnyRates.forEach(rate => addVehicle(rate, 3, false));
    
    // Convert map to array and sort by rate
    const vehicles = Array.from(vehicleMap.values());
    vehicles.sort((a, b) => (a.total || 0) - (b.total || 0));
    
    return vehicles;
  }, [allTransferRates, formData.no_nights]);

  // Update vehicleOptions when route changes
  useEffect(() => {
    if (allTransferRates.length > 0) {
      const routeId = formData.route_id;
      const arrivalCity = formData.arrival_city;
      const departureCity = formData.departure_city || arrivalCity;
      const transferDays = calculationResult?.transfer_days || formData.no_nights + 1 || 5;
      
      const options = getVehicleOptionsForRoute(routeId, arrivalCity, departureCity, transferDays);
      setVehicleOptions(options);
    }
  }, [allTransferRates, formData.route_id, formData.arrival_city, formData.departure_city, formData.no_nights, calculationResult?.transfer_days, getVehicleOptionsForRoute]);

  const fetchCategoryBands = async () => {
    try {
      const response = await api.get('/admin/category-bands');
      if (response.data && response.data.length > 0) {
        // Store full category band data for price-based matching
        setCategoryBands(response.data);
        // Extract category names for display in UI, sorted by display_order or min_rate
        const sortedBands = [...response.data].sort((a, b) => 
          (a.display_order ?? a.min_rate) - (b.display_order ?? b.min_rate)
        );
        const categoryNames = sortedBands.map(band => band.category_name);
        setAvailableCategories(categoryNames);
        console.log('Category bands loaded from Masters:', categoryNames);
      } else {
        // Fallback to defaults if no bands configured
        setAvailableCategories(DEFAULT_ROOM_CATEGORIES);
      }
    } catch (error) {
      // Keep default categories if fetch fails
      console.error('Failed to load category bands, using defaults');
      setAvailableCategories(DEFAULT_ROOM_CATEGORIES);
    }
  };

  // Switch between STAR and PRICE_RANGE classification modes
  const switchClassificationMode = (newMode) => {
    setClassificationMode(newMode);
    if (newMode === 'STAR') {
      // Switch to star category tiers
      const starLabels = STAR_CATEGORY_TIERS.map(t => t.label);
      setAvailableCategories(starLabels);
      setFormData(prev => ({
        ...prev,
        classification_mode: 'STAR',
        selected_categories: ['4★'],
        hotel_category: '4★'
      }));
    } else {
      // Switch to price range categories
      const priceCategories = categoryBands.length > 0 
        ? categoryBands.sort((a, b) => a.min_rate - b.min_rate).map(b => b.category_name)
        : DEFAULT_ROOM_CATEGORIES;
      setAvailableCategories(priceCategories);
      setFormData(prev => ({
        ...prev,
        classification_mode: 'PRICE_RANGE',
        selected_categories: ['Deluxe'],
        hotel_category: 'Deluxe'
      }));
    }
  };

  const fetchMasters = async () => {
    if (isB2BAgent) return;
    try {
      const response = await api.get('/admin/masters');
      setMasters(response.data);
      const inclusionMaster = response.data.find(m => m.master_type === 'INCLUSION' && m.is_default);
      const exclusionMaster = response.data.find(m => m.master_type === 'EXCLUSION' && m.is_default);
      const paymentMaster = response.data.find(m => m.master_type === 'PAYMENT' && m.is_default);
      const cancellationMaster = response.data.find(m => m.master_type === 'CANCELLATION' && m.is_default);
      const termsMaster = response.data.find(m => m.master_type === 'TERMS' && m.is_default);
      
      setFormData(prev => ({
        ...prev,
        inclusions: inclusionMaster?.content || '',
        exclusions: exclusionMaster?.content || '',
        payment_terms: paymentMaster?.content || '',
        cancellation_policy: cancellationMaster?.content || '',
        terms_conditions: termsMaster?.content || '',
      }));
    } catch (error) {
      console.error('Failed to load masters');
    }
  };

  const fetchAvailableMealPlans = async (routeId, categories) => {
    if (!routeId || categories.length === 0) {
      setAvailableMealPlans(MEAL_PLANS);
      return;
    }
    try {
      const response = await api.get('/hotel-rates/available-meal-plans', {
        params: { route_id: routeId, categories: categories.join(',') }
      });
      const available = response.data?.available_meal_plans || [];
      if (available.length > 0) {
        const filtered = MEAL_PLANS.filter(mp => available.includes(mp.value));
        setAvailableMealPlans(filtered.length > 0 ? filtered : MEAL_PLANS);
        setFormData(prev => ({
          ...prev,
          meal_plans: prev.meal_plans.filter(mp => available.includes(mp))
        }));
      } else {
        setAvailableMealPlans(MEAL_PLANS);
      }
    } catch (error) {
      setAvailableMealPlans(MEAL_PLANS);
    }
  };

  const fetchAvailableCategories = async (routeId) => {
    // Categories should ALWAYS come from Masters (Room Category Bands)
    // This function is now deprecated - categories are set by fetchCategoryBands
    // We no longer filter based on route because the pricing engine will handle
    // matching hotel rates by price bands, not by category name
    console.log('Categories loaded from Masters - no route-based filtering');
  };

  // Fetch actual star ratings from hotels in database
  const fetchStarRatings = async () => {
    try {
      const response = await api.get('/admin/hotel-star-ratings');
      if (response.data && response.data.length > 0) {
        setStarCategoryTiers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch star ratings:', error);
      // Keep default tiers as fallback
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchRoutes();
    fetchPlaces();
    fetchMasters();
    fetchCategoryBands();  // Fetch category bands from Masters
    fetchStarRatings();    // Fetch actual star ratings from hotels
    if (!isB2BAgent) {
      fetchTravelAgents();
    }
    fetchVehicleOptions();
    
    // Check for clone data in sessionStorage after a small delay
    // to ensure form state is initialized
    const timer = setTimeout(() => {
      loadCloneData();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isB2BAgent]);
  
  // Saved custom package -> wizard state: stops, per-option hotel picks, shared vehicle.
  const customPackageSeed = (wd) => {
    const pkg = wd.custom_package_data || {};
    const stops = (pkg.stops || []).map(({ hotels, ...s }) => s);
    const toSelections = (optStops) => Object.fromEntries((optStops || []).map((s, i) => [
      `stop_${i}`, { check_in: s.check_in, check_out: s.check_out, hotels: s.hotels || [] },
    ]));
    const saved = (pkg.options || []).length ? pkg.options : [{ option_name: 'Option 1', stops: pkg.stops }];
    const options = saved.map((o, i) => ({
      id: Date.now() + i, option_name: o.option_name || `Option ${i + 1}`,
      selected_hotels: toSelections(o.stops), collapsed: i > 0,
    }));
    const nights = stops.reduce((n, s) => n + (s.nights || 0), 0);
    return {
      form: {
        custom_package_data: { ...pkg, stops },
        custom_package_options: options,
        base_vehicle_type: wd.base_vehicle_type || wd.vehicle_type_selected,
        vehicle_counts: wd.vehicle_counts || {},
        transfer_days: wd.transfer_days,
        transfer_days_auto: !wd.transfer_days || wd.transfer_days === nights + 1,
      },
      selections: options[0]?.selected_hotels || {},
    };
  };

  // Load clone data from sessionStorage if available
  const loadCloneData = () => {
    try {
      const cloneDataStr = sessionStorage.getItem('clone_quotation_data');
      console.log('Clone data from sessionStorage:', cloneDataStr ? 'Found' : 'Not found');
      if (cloneDataStr) {
        const cloneData = JSON.parse(cloneDataStr);
        sessionStorage.removeItem('clone_quotation_data'); // Clean up
        
        console.log('Loading clone data:', cloneData.clone_reference);
        
        if (cloneData.wizard_data) {
          const wd = cloneData.wizard_data;
          
          // Pre-populate form with clone data
          const isVersion = cloneData.mode === 'version';
          const custom = wd.route_type === 'custom_package' ? customPackageSeed(wd) : {};
          setFormData(prev => ({
            ...prev,
            // Clone / new-version metadata
            // A clone is an unrelated new quote, so it is not grouped under its source.
            parent_quote_id: null,
            clone_of: isVersion ? null : cloneData.source_reference,
            clone_sequence: 0,
            clone_reference: null,
            version_of: isVersion ? cloneData.version_of : null,
            source_reference: cloneData.source_reference,
            source_version_label: cloneData.source_version_label,
            next_version_label: cloneData.next_version_label,
            ...custom.form,
            // Guest details
            guest_name: wd.guest_name || '',
            guest_phone: wd.guest_phone || '',
            guest_email: wd.guest_email || '',
            guest_country_code: wd.guest_country_code || '+91',
            // Route details
            route_type: wd.route_type || 'predefined',
            route_id: wd.route_id,
            custom_route: wd.custom_route_json || [],
            arrival_city: wd.arrival_city || '',
            arrival_point: wd.arrival_point || 'Airport',
            arrival_date: wd.arrival_date || '',
            departure_city: wd.departure_city || '',
            // Passengers
            no_adults: wd.no_adults || 2,
            no_children_with_bed: wd.no_children_with_bed || 0,
            no_children_no_bed: wd.no_children_no_bed || 0,
            no_infants: wd.no_infants || 0,
            // Room config
            single_rooms: wd.single_rooms || 0,
            double_rooms: wd.double_rooms || 1,
            triple_rooms: wd.triple_rooms || 0,
            // Categories and meal plans
            hotel_category: wd.hotel_category || 'Deluxe',
            selected_categories: wd.selected_categories || ['Deluxe'],
            meal_plans: wd.meal_plans || ['CP'],
            classification_mode: wd.classification_mode || 'PRICE_RANGE',
            // Vehicle
            vehicle_type_selected: wd.vehicle_type_selected,
            // Pricing config
            markup_pct: wd.markup_pct || 0,
            ...fromCanonicalMarkupType(wd.markup_type, wd.markup_per_person),
            markup_amount: wd.markup_amount || 0,
            discount_pct: wd.discount_pct || 0,
            discount_type: wd.discount_type || 'percent',
            discount_amount: wd.discount_amount || 0,
            discount_per_person: wd.discount_per_person || false,
            gst_percentage: wd.gst_percentage || 0,
            gst_included: wd.gst_included !== false,
            gst_apply_on: wd.gst_apply_on || 'total',
            // Ticket #285275 (#9/#10): saved 'per_person' -> form value 'person_wise'
            // so the format radio is selected when a saved quote is re-opened.
            quotation_format: wd.quotation_format === 'per_person' ? 'person_wise' : (wd.quotation_format || 'total'),
            show_breakup: wd.show_breakup ?? isB2BAgent,
            show_discount_separately: wd.show_discount_separately || false,
            // Misc costs
            misc_costs: (wd.misc_costs || []).map((m, i) => ({ ...m, id: m.id || `${Date.now()}-${i}` })),
            // Terms
            inclusions: wd.inclusions || '',
            exclusions: wd.exclusions || '',
            payment_terms: wd.payment_terms || '',
            cancellation_policy: wd.cancellation_policy || '',
            terms_conditions: wd.terms_conditions || '',
            // Overrides
            hotel_overrides: wd.hotel_overrides,
            p2p_transfers: wd.p2p_transfers,
            payment_policy_id: wd.payment_policy_id || prev.payment_policy_id,
            show_misc_table: wd.show_misc_table !== false,
          }));
          if (custom.selections) setCustomPackageHotelSelections(custom.selections);
          
          if (wd.classification_mode) {
            setClassificationMode(wd.classification_mode);
          }
          
          // Ticked uplift vehicles carry over (custom); predefined keeps the base pre-ticked.
          if (wd.route_type === 'custom_package') {
            setSelectedVehicles(wd.selected_vehicles || []);
          } else if (wd.vehicle_type_selected) {
            setSelectedVehicles([...new Set([wd.vehicle_type_selected, ...(wd.selected_vehicles || [])])]);
          }
          
          toast.success(isVersion
            ? `Creating ${cloneData.next_version_label} of ${cloneData.source_reference}`
            : `Loaded ${cloneData.source_reference} as a new quote`);
        }
      }
    } catch (error) {
      console.error('Failed to load clone data:', error);
    }
  };

  // Fetch available options when route/categories change
  useEffect(() => {
    if (formData.route_id && formData.selected_categories.length > 0) {
      fetchAvailableMealPlans(formData.route_id, formData.selected_categories);
    }
  }, [formData.route_id, formData.selected_categories]);

  useEffect(() => {
    if (formData.route_id) {
      fetchAvailableCategories(formData.route_id);
    }
  }, [formData.route_id]);

  // Form handlers
  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const toggleMealPlan = (mealPlanValue) => {
    setFormData(prev => {
      const selected = prev.meal_plans.includes(mealPlanValue)
        ? prev.meal_plans.filter(mp => mp !== mealPlanValue)
        : [...prev.meal_plans, mealPlanValue];
      return { ...prev, meal_plans: selected.length > 0 ? selected : [mealPlanValue] };
    });
  };

  const toggleCategory = (category) => {
    setFormData((prev) => {
      const selected = prev.selected_categories.includes(category)
        ? prev.selected_categories.filter((c) => c !== category)
        : [...prev.selected_categories, category];
      let primaryCategory = prev.hotel_category;
      if (!selected.includes(primaryCategory)) {
        primaryCategory = selected[0] || 'DELUXE';
      }
      return {
        ...prev,
        selected_categories: selected,
        hotel_category: primaryCategory,
      };
    });
  };

  const addCustomStop = () => {
    setFormData((prev) => ({
      ...prev,
      custom_route: [
        ...prev.custom_route,
        { place_id: '', place_name: '', nights: 1, order_seq: prev.custom_route.length + 1 },
      ],
    }));
  };

  const removeCustomStop = (index) => {
    setFormData((prev) => ({
      ...prev,
      custom_route: prev.custom_route.filter((_, i) => i !== index),
    }));
  };

  // Accepts a patch object: updateCustomStop(index, { place_id, place_name } | { nights })
  const updateCustomStop = (index, patch) => {
    setFormData((prev) => {
      const newRoute = [...prev.custom_route];
      newRoute[index] = { ...newRoute[index], ...patch };
      return { ...prev, custom_route: newRoute };
    });
  };

  // Step navigation
  const nextStep = () => {
    // Step 1 is now Guest & Trip (merged)
    if (step === 1) {
      // Validate guest info (optional but recommended)
      if (!formData.guest_name) {
        toast.error('Please enter guest name');
        return;
      }
      
      // Validate based on route type
      if (formData.route_type === 'predefined') {
        if (!formData.route_id) {
          toast.error('Please select a route');
          return;
        }
        if (!formData.arrival_city || !formData.arrival_date) {
          toast.error('Please fill in arrival details');
          return;
        }
      } else if (formData.route_type === 'custom') {
        if (formData.custom_route.length === 0) {
          toast.error('Please add at least one stop');
          return;
        }
        if (!formData.arrival_city || !formData.arrival_date) {
          toast.error('Please fill in arrival details');
          return;
        }
      } else if (formData.route_type === 'custom_package') {
        // Validate custom package data
        const pkg = formData.custom_package_data;
        if (!pkg) {
          toast.error('Custom package data not found. Please configure your package.');
          return;
        }
        // Check arrival_city in both pkg and formData (sync may not have completed yet)
        if (!pkg.arrival_city && !formData.arrival_city) {
          toast.error('Please select an arrival city');
          return;
        }
        // Check arrival_date in both pkg and formData
        if (!pkg.arrival_date && !formData.arrival_date) {
          toast.error('Please select an arrival date');
          return;
        }
        if (!pkg.stops || pkg.stops.length === 0) {
          toast.error('Please add at least one stop to your package');
          return;
        }
      } else if (formData.route_type === 'dmc_package') {
        // DMC package validation
        if (!formData.dmc_content?.parsed) {
          toast.error('Please parse or enter DMC package details');
          return;
        }
      } else if (formData.route_type === 'manual_package') {
        // Manual package validation
        const manualData = formData.manual_package_data;
        if (!manualData || !manualData.route || manualData.route.length === 0) {
          toast.error('Please add at least one stop to your manual package');
          return;
        }
        if (!formData.arrival_date) {
          toast.error('Please select an arrival date');
          return;
        }
      }
    }
    // For Step 2 → Step 3, we require calculationResult
    if (step === 2 && !calculationResult) {
      toast.error('Please calculate the quote first');
      return;
    }
    setStep((prev) => Math.min(prev + 1, 3));
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));
  
  // Direct step navigation (for tab clicks)
  const goToStep = (targetStep) => {
    // Allow backward navigation freely
    if (targetStep < step) {
      setStep(targetStep);
      return;
    }
    // Step 3 requires calculation result
    if (targetStep === 3 && !calculationResult) {
      toast.error('Please calculate the quote first');
      return;
    }
    // Once a calculation exists, Step 1 has already been validated — allow
    // jumping straight back to Preferences or Preview. Without this the
    // operator can't return to Preview after fixing pax/dates on Step 1,
    // which is exactly the path the stale-calc guard exists to cover.
    if (calculationResult && targetStep <= 3) {
      setStep(targetStep);
      return;
    }
    // Forward navigation via tabs is limited to completed steps + 1
    if (targetStep <= step + 1 && targetStep <= 3) {
      setStep(targetStep);
    }
  };
  
  // Reset calculation result when changing route/preferences (invalidate Step 3)
  const invalidateCalculation = useCallback(() => {
    setCalculationResult(null);
  }, []);

  // Derived values
  const totalPax = formData.no_adults + formData.no_children_with_bed + formData.no_children_no_bed + formData.no_infants;
  const paxNeedingBeds = formData.no_adults + formData.no_children_with_bed;
  const suggestedSingleRooms = paxNeedingBeds === 1 ? 1 : 0;
  const suggestedDoubleRooms = paxNeedingBeds === 1 ? 0 : Math.floor(paxNeedingBeds / 2);
  const suggestedTripleRooms = 0;
  const extraBeds = paxNeedingBeds === 1 ? 0 : paxNeedingBeds % 2;

  const calculateDepartureDate = () => {
    if (!formData.arrival_date || !formData.route_id) return '';
    const route = routes.find(r => r.route_id === formData.route_id);
    if (!route) return '';
    const arrivalDate = new Date(formData.arrival_date);
    arrivalDate.setDate(arrivalDate.getDate() + route.total_days - 1);
    return arrivalDate.toISOString().split('T')[0];
  };

  const departureDate = calculateDepartureDate();

  const getRouteDuration = () => {
    if (!formData.route_id) return null;
    const route = routes.find(r => r.route_id === formData.route_id);
    return route ? { days: route.total_days, nights: route.total_nights } : null;
  };

  const routeDuration = getRouteDuration();

  return {
    // State
    step,
    loading,
    calculating,
    routes,
    places,
    travelAgents,
    calculationResult,
    availableMealPlans,
    availableCategories,
    categoryBands,
    starCategoryTiers,
    classificationMode,
    vehicleOptions,
    selectedVehicles,
    customPackageHotelSelections,
    masters,
    formData,
    user,
    isB2BAgent,
    showCostBreakdown,
    
    // Setters
    setStep,
    setLoading,
    setCalculating,
    setCalculationResult,
    setCustomPackageHotelSelections,
    setSelectedVehicles,
    setFormData,
    
    // Handlers
    handleInputChange,
    toggleMealPlan,
    toggleCategory,
    addCustomStop,
    removeCustomStop,
    updateCustomStop,
    nextStep,
    prevStep,
    goToStep,
    invalidateCalculation,
    switchClassificationMode,
    
    // Refresh functions
    refreshPlaces: fetchPlaces,
    
    // Derived
    totalPax,
    paxNeedingBeds,
    suggestedSingleRooms,
    suggestedDoubleRooms,
    suggestedTripleRooms,
    extraBeds,
    departureDate,
    routeDuration,
    
    // Constants
    ROOM_CATEGORIES: DEFAULT_ROOM_CATEGORIES,
    STAR_CATEGORY_TIERS,
    MEAL_PLANS,
  };
};

export default useQuotationForm;
